/**
 * Paleta fixa das tags (PLANO_TAGS D8) e a cor do texto do chip.
 *
 * O fundo do chip é a própria cor da tag, igual nos temas claro e escuro; o texto é
 * branco ou quase preto, o que der mais contraste. Toda cor da paleta passa AA
 * (4,5:1) com o texto escolhido — `__tests__/tagsLib.test.ts` confere. O backend
 * aceita qualquer `#RRGGBB`: uma cor fora da paleta (vinda da API antiga) ainda
 * ganha o texto de maior contraste.
 */

export interface CorTag {
  hex: string;
  nome: string;
}

// Tons pastéis (pedido do Adriano em 2026-10-20: as cores vivas gritavam na lista).
// Com fundo claro, o texto escolhido é o escuro — e o contraste passa AA com folga.
export const CORES_TAG: CorTag[] = [
  { hex: '#FECACA', nome: 'Vermelho' },
  { hex: '#FED7AA', nome: 'Laranja' },
  { hex: '#FDE68A', nome: 'Âmbar' },
  { hex: '#BBF7D0', nome: 'Verde' },
  { hex: '#99F6E4', nome: 'Verde-azulado' },
  { hex: '#BFDBFE', nome: 'Azul' },
  { hex: '#C7D2FE', nome: 'Índigo' },
  { hex: '#DDD6FE', nome: 'Roxo' },
  { hex: '#FBCFE8', nome: 'Rosa' },
  { hex: '#E2E8F0', nome: 'Cinza' },
];

/** Cor das tags sem cor (as que vieram do texto livre dos documentos). */
export const COR_NEUTRA = '#E2E8F0';

const TEXTO_CLARO = '#FFFFFF';
const TEXTO_ESCURO = '#111827';

function canal(valor: number): number {
  const c = valor / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function luminancia(hex: string): number {
  const limpo = hex.replace('#', '');
  const r = parseInt(limpo.slice(0, 2), 16);
  const g = parseInt(limpo.slice(2, 4), 16);
  const b = parseInt(limpo.slice(4, 6), 16);
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

/** Razão de contraste WCAG entre duas cores `#RRGGBB`. */
export function contraste(a: string, b: string): number {
  const [l1, l2] = [luminancia(a), luminancia(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
}

export function corValida(cor: string | null | undefined): string {
  return cor && /^#[0-9A-Fa-f]{6}$/.test(cor) ? cor : COR_NEUTRA;
}

/** Cor do texto sobre o fundo da tag: a de maior contraste. */
export function corDoTexto(fundo: string | null | undefined): string {
  const cor = corValida(fundo);
  return contraste(cor, TEXTO_CLARO) >= contraste(cor, TEXTO_ESCURO) ? TEXTO_CLARO : TEXTO_ESCURO;
}
